import React, { FC, useContext, useMemo, useState, useCallback } from "react";
import "./TaskChecklist.css";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import { Context } from "../../../index";
import { useParams } from "react-router-dom";
import TaskChecklistGroup from "../TaskChecklistGroup/TaskChecklistGroup";
import { AppContext } from "../../../App";
import SaveInputValue from "../../../assets/save_input_value.svg";

import axios from "axios";
import { hasPermission } from "../../../helpers/hasPermisson";
import { IOptionsQueryParams } from "../../../helpers/paramsAutoFiller";
import type { ITaskChecklistEdit } from "../../../common/api_interfaces/taskChecklist/ITaskChecklistEdit";
import type { ITaskChecklist } from "../../../common/api_interfaces/taskChecklist/ITaskChecklist";
import type { ITaskChecklistGroup } from "../../../common/api_interfaces/taskChecklist/ITaskChecklistGroup";
import { handleError } from "../../../helpers/ErrorLib/handleError";
import { ITaskChecklistGroupEdit } from "../../../common/api_interfaces/taskChecklist/ITaskChecklistGroupEdit";

import ExitCross from "../../../assets/cancelGreey.svg";

import { ReactComponent as AddDefault } from "../../../assets/images/addDefault.svg";
import Button from "../../UiLib/Button/Button";

interface TaskCheckListProps {
    systemUserAccess: IFunctionPermissions[];
    userAccess: IFunctionPermissions[];
    checklistGroups: ITaskChecklistGroup[];
    loadChecklist: () => Promise<void>;
    openAllDropdown?: boolean;
    optionsQueryParams: IOptionsQueryParams;
}

interface EditGroup {
    checklistGroupId: number;
    newName?: string;
    newPosition?: number;
}

interface EditElement {
    checklistGroupId: number;
    checklistElementId: number;
    newName?: string;
    newPosition?: number;
}

const TaskChecklist: FC<TaskCheckListProps> = ({
    systemUserAccess,
    userAccess,
    checklistGroups,
    loadChecklist,
    openAllDropdown,
    optionsQueryParams,
}) => {
    const { store } = useContext(Context);
    const { showToast, handleShowLoader } = useContext(AppContext);
    const { id } = useParams();

    const [showAddGroupInput, setShowAddGroupInput] = useState(false);
    const [showAddElementInput, setShowElementInput] = useState(false);
    const [nameGroup, setNameGroup] = useState("");
    const [nameElement, setNameElement] = useState("");
    const [isLowerElementDrag, setIsLowerElementDrag] = useState(false);
    const [isUpperElementDrag, setIsUpperElementDrag] = useState(false);
    const [checklistGroup, setChecklistGroup] = useState<ITaskChecklistGroup>();
    const [showElementsArr, setShowElementsArr] = useState<number[]>(
        openAllDropdown && checklistGroup ? [checklistGroup.id] : []
    );
    const [dragEventTarget, setDragEventTarget] = useState<EventTarget | null>(null);
    const [previewChecklistGroupIndex, setPreviewChecklistGroupIndex] = useState<number>();

    const createChekListTask = useMemo(() => hasPermission(systemUserAccess, "ChecklistAction", "create") || hasPermission(userAccess, "ChecklistAction", "create"), [userAccess, systemUserAccess]);

    const taskId = Number(id!);
    const sortedChecklistGroups = useMemo(() =>
        [...checklistGroups].sort((a, b) => a.position - b.position),
        [checklistGroups]
    );

    const removePreview = useCallback(() => {
        const elements = document.querySelectorAll(".column-preview");
        elements.forEach(element => element.remove());
        setPreviewChecklistGroupIndex(undefined);
    }, []);

    const showElementsList = useCallback((groupId: number) => {
        setShowElementsArr(prev =>
            prev.includes(groupId)
                ? prev.filter(id => id !== groupId)
                : [...prev, groupId]
        );
    }, []);

    const createPreviewElement = useCallback((content: string) => {
        const newElement = document.createElement("div");
        newElement.className = "column-preview";
        newElement.innerHTML = content;
        // newElement.style.backgroundColor = "black"; //#149cce6b
        // 
        // newElement.style.margin = "5px";
        // // newElement.style.padding = "px";
        // newElement.style.borderRadius = "6px";
        // newElement.style.pointerEvents = "none";
        return newElement;
    }, []);

    const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragEventTarget(e.target);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (dragEventTarget !== e.target) {
            removePreview();
        }
    }, [dragEventTarget, removePreview]);

    const handleDragEnd = useCallback(() => {
        removePreview();
    }, [removePreview]);

    const findInsertIndexForGroups = useCallback((clientY: number) => {
        for (let i = 0; i < sortedChecklistGroups.length; i++) {
            const groupElement = document.getElementById(`group-${sortedChecklistGroups[i].id}`);
            if (groupElement) {
                const rect = groupElement.getBoundingClientRect();
                const midY = rect.top + rect.height / 2;
                if (clientY < midY) {
                    return i;
                }
            }
        }
        return sortedChecklistGroups.length;
    }, [sortedChecklistGroups]);

    const handleGroupDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (e.dataTransfer.types[0] !== "application/json") return;

        const insertIndex = findInsertIndexForGroups(e.clientY);

        if (previewChecklistGroupIndex === insertIndex) return;

        removePreview();

        const targetElement = insertIndex < sortedChecklistGroups.length
            ? document.getElementById(`group-${sortedChecklistGroups[insertIndex].id}`)
            : document.getElementById(`group-${sortedChecklistGroups[sortedChecklistGroups.length - 1].id}`);

        if (targetElement) {
            const newElement = createPreviewElement("<div></div>");
            const insertMethod = insertIndex < sortedChecklistGroups.length ? "beforebegin" : "afterend";
            targetElement.insertAdjacentElement("beforebegin", newElement);
            setPreviewChecklistGroupIndex(insertIndex);
        }
    }, [sortedChecklistGroups, previewChecklistGroupIndex, removePreview, createPreviewElement, findInsertIndexForGroups]);

    const handleElementDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (e.dataTransfer.types[0] !== "application/json") return;

        const clientY = e.clientY;
        let targetGroup: ITaskChecklistGroup | undefined;

        // Находим целевую группу
        for (const group of sortedChecklistGroups) {
            const groupElement = document.getElementById(`group-${group.id}`);
            if (groupElement) {
                const rect = groupElement.getBoundingClientRect();
                if (clientY > rect.top && clientY < rect.bottom) {
                    targetGroup = group;
                    break;
                }
            }
        }

        if (!targetGroup) return;

        const groupElement = document.getElementById(`group-${targetGroup.id}`);
        if (!groupElement) return;

        // Показываем элементы если скрыты
        const tasksElement = groupElement.querySelector(".task_checklist_element") as HTMLElement;
        if (tasksElement && tasksElement.style.display === "none") {
            showElementsList(targetGroup.id);
        }

        // Находим индекс для вставки
        let insertIndex = targetGroup.checklist?.length || 0;

        if (targetGroup.checklist && targetGroup.checklist.length > 0) {
            for (let i = 0; i < targetGroup.checklist.length; i++) {
                const element = document.getElementById(`element-${targetGroup.checklist[i].id}`);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    const midY = rect.top + rect.height / 2;

                    if (clientY < midY) {
                        insertIndex = i; // Вставить перед этим элементом
                        break;
                    } else {
                        insertIndex = i + 1; // Вставить после этого элемента
                    }
                }
            }
        }

        if (previewChecklistGroupIndex === insertIndex) return;

        removePreview();

        // Создаем preview элемент
        const previewElement = createPreviewElement("<div></div>");

        if (targetGroup.checklist && targetGroup.checklist.length > 0 && insertIndex < targetGroup.checklist.length) {
            // Вставляем перед существующим элементом
            const targetElement = document.getElementById(`element-${targetGroup.checklist[insertIndex].id}`);
            if (targetElement) {
                targetElement.insertAdjacentElement("beforebegin", previewElement);
            }
        } else if (targetGroup.checklist && targetGroup.checklist.length > 0) {
            // Вставляем после последнего элемента
            const lastElement = document.getElementById(`element-${targetGroup.checklist[targetGroup.checklist.length - 1].id}`);
            if (lastElement) {
                lastElement.insertAdjacentElement("afterend", previewElement);
            }
        } else {
            // Группа пустая - вставляем в контейнер
            const taskContainer = groupElement.querySelector(".task_checklist_element");
            if (taskContainer) {
                taskContainer.appendChild(previewElement);
            }
        }

        setPreviewChecklistGroupIndex(insertIndex);
    }, [sortedChecklistGroups, previewChecklistGroupIndex, removePreview, createPreviewElement, showElementsList]);

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        if (!isLowerElementDrag) handleGroupDragOver(e);
        if (!isUpperElementDrag) handleElementDragOver(e);
    }, [isLowerElementDrag, isUpperElementDrag, handleGroupDragOver, handleElementDragOver]);

    const editChecklistElement = useCallback(async (editData: EditElement) => {
        try {
            const element: ITaskChecklistEdit = {
                id: editData.checklistElementId,
                groupId: editData.checklistGroupId,
                position: editData.newPosition,
            };
            await store.taskService.editTaskChecklistElement(element);
            await loadChecklist();
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message);
            }
        }
    }, [store.taskService, loadChecklist, showToast]);

    const editGroup = useCallback(async (editData: EditGroup) => {
        try {
            const group: ITaskChecklistGroupEdit = {
                id: editData.checklistGroupId,
                name: editData.newName,
                position: editData.newPosition,
            };
            await store.taskService.editChecklistGroup(group);
            await loadChecklist();
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message);
            }
        }
    }, [store.taskService, loadChecklist, showToast]);

    const showPermissionToast = useCallback(() => {
        showToast("Вы не можете добавлять элементы чек листа пока у вас нет прав на создание чеклиста");
    }, [showToast]);

    const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();

        try {
            const data = JSON.parse(e.dataTransfer.getData("application/json"));
            const { checklistGroupId, checklistElementId } = data;
            const clientY = e.clientY;

            if (checklistElementId) {
                // Обработка перетаскивания элемента
                await handleElementDrop(checklistGroupId, checklistElementId, clientY);
            } else {
                // Обработка перетаскивания группы
                await handleGroupDrop(checklistGroupId, clientY);
            }
        } catch (err) {
            console.error("Drop error:", err);
        } finally {
            removePreview();
        }
    }, [sortedChecklistGroups, removePreview]);

    const handleElementDrop = useCallback(async (checklistGroupId: number, checklistElementId: number, clientY: number) => {
        const movedChecklistGroup = sortedChecklistGroups.find(c => c.id === checklistGroupId);
        if (!movedChecklistGroup) return;

        let targetGroup: ITaskChecklistGroup | undefined;
        let insertIndex = 0;

        // Находим целевую группу
        for (const group of sortedChecklistGroups) {
            const groupElement = document.getElementById(`group-${group.id}`);
            if (groupElement) {
                const rect = groupElement.getBoundingClientRect();
                if (clientY > rect.top && clientY < rect.bottom) {
                    targetGroup = group;
                    break;
                }
            }
        }

        if (!targetGroup) return;

        // Находим индекс вставки в целевой группе
        if (targetGroup.checklist && targetGroup.checklist.length > 0) {
            for (let i = 0; i < targetGroup.checklist.length; i++) {
                const element = document.getElementById(`element-${targetGroup.checklist[i].id}`);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    const midY = rect.top + rect.height / 2;

                    if (clientY < midY) {
                        insertIndex = i;
                        break;
                    } else {
                        insertIndex = i + 1;
                    }
                }
            }

            // Корректируем индекс если перемещаем внутри той же группы
            if (targetGroup.id === checklistGroupId) {
                const currentIndex = targetGroup.checklist.findIndex(c => c.id === checklistElementId);
                if (insertIndex > currentIndex) insertIndex--;
                if (insertIndex === currentIndex) return;
            }
        }

        // Обновляем элемент
        const editData: EditElement = {
            checklistElementId,
            checklistGroupId: targetGroup.id,
            newPosition: insertIndex,
        };

        await editChecklistElement(editData);
    }, [sortedChecklistGroups, editChecklistElement]);

    const handleGroupDrop = useCallback(async (checklistGroupId: number, clientY: number) => {
        const movedChecklistGroup = sortedChecklistGroups.find(c => c.id === checklistGroupId);
        if (!movedChecklistGroup) return;

        const insertIndex = findInsertIndexForGroups(clientY);
        const currentIndex = sortedChecklistGroups.indexOf(movedChecklistGroup);

        if (insertIndex === currentIndex) return;

        const editData: EditGroup = {
            checklistGroupId,
            newPosition: insertIndex,
        };

        await editGroup(editData);
    }, [sortedChecklistGroups, findInsertIndexForGroups, editGroup]);

    const toggleAddGroupInput = useCallback(() => {
        setShowAddGroupInput(prev => !prev);
        setShowElementInput(false);
    }, []);

    const toggleAddElementInput = useCallback(() => {
        setShowElementInput(prev => !prev);
        setShowAddGroupInput(false);
    }, []);

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (showAddGroupInput) {
            setNameGroup(value);
        } else if (showAddElementInput) {
            setNameElement(value);
        }
    }, [showAddGroupInput, showAddElementInput]);

    const handleKeyDown = useCallback(async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            await createItem();
        }
    }, []);

    const createItem = useCallback(async () => {
        if (showAddGroupInput && nameGroup.trim()) {
            try {
                handleShowLoader(true);
                const group: ITaskChecklistGroup = {
                    id: -1,
                    name: nameGroup.trim(),
                    position: 0,
                    taskId,
                };
                await store.taskService.createChecklistGroup(group);
                await loadChecklist();
                setNameGroup("");
                setShowAddGroupInput(false);
            } catch (error) {
                handleError(error, {
                    defaultMessage: "Неизвестная ошибка при создании группы",
                    showToast: showToast
                });
            } finally {
                handleShowLoader(false);
            }
        } else if (showAddElementInput && nameElement.trim()) {
            await createElement();
        }
    }, [showAddGroupInput, showAddElementInput, nameGroup, nameElement, taskId, store.taskService, loadChecklist, showToast]);

    const createElement = useCallback(async (groupId?: number, newElementName?: string) => {
        const name = newElementName?.trim() || nameElement.trim();
        if (!name) return;

        try {
            handleShowLoader(true);
            const targetGroupId = groupId || sortedChecklistGroups.find(x => x.position === 0)?.id;

            if (!targetGroupId) return;

            const element: ITaskChecklist = {
                id: -1,
                text: name,
                position: 0,
                checked: false,
                chatId: -1,
                taskId,
                groupId: targetGroupId,
            };

            await store.taskService.createChecklistElement(element);
            await loadChecklist();
            setNameElement("");
            setShowElementInput(false);
        } catch (error) {
            handleError(error, {
                defaultMessage: "Неизвестная ошибка при создании пункта",
                showToast: showToast
            });
        } finally {
            handleShowLoader(false);
        }
    }, [nameElement, sortedChecklistGroups, taskId, store.taskService, loadChecklist, showToast]);

    return (
        <div className="checklist--body">
            <div className="checklist_button_add--body">
                <div
                    className={createChekListTask ? "checklist_button_add" : "checklist_button_add_gray"}
                    onClick={createChekListTask ? toggleAddElementInput : showPermissionToast}
                >
                    <div>Добавить пункт</div>
                    <Button
                        id={createChekListTask ? "button-cross" : "button-cross-gray"}
                        callBack={createChekListTask ? toggleAddElementInput : showPermissionToast}
                        image={<AddDefault />}
                    />
                </div>
                <div
                    className={createChekListTask ? "checklist_button_add" : "checklist_button_add_gray"}
                    onClick={createChekListTask ? toggleAddGroupInput : showPermissionToast}
                >
                    <div>Добавить группу</div>
                    <Button
                        id={createChekListTask ? "button-cross" : "button-cross-gray"}
                        callBack={createChekListTask ? toggleAddGroupInput : showPermissionToast}
                        image={<AddDefault />}
                    />
                </div>
            </div>

            {(showAddGroupInput || showAddElementInput) && (
                <div className={showAddGroupInput ? "task_checklist_group--input" : "task_checklist_elem--input"}>
                    <div className={showAddGroupInput ? "task_checklist_group__block--name-add" : "task_checklist_elem__block--name-add"}>
                        <input
                            type="text"
                            value={showAddGroupInput ? nameGroup : nameElement}
                            onChange={handleNameChange}
                            onKeyDown={handleKeyDown}
                            placeholder={showAddGroupInput ? "Введите название группы" : "Введите название пункта"}
                            autoFocus
                        />
                    </div>
                    <button
                        className="grey-cancel-button"
                        onClick={showAddGroupInput ? toggleAddGroupInput : toggleAddElementInput}
                    >
                        <img src={ExitCross} alt="Cancel" />
                    </button>
                    <button
                        className="postive-green-button right-aligned"
                        onClick={createItem}
                        disabled={!((showAddGroupInput && nameGroup.trim()) || (showAddElementInput && nameElement.trim()))}
                    >
                        <img src={SaveInputValue} alt="Save" />
                    </button>
                </div>
            )}
            <div
                className="task_checklist_groups"
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragLeave={handleDragLeave}
                onDragEnd={handleDragEnd}
            >
                {sortedChecklistGroups.map((group, index) => (
                    <TaskChecklistGroup
                        key={group.id}
                        checklistGroup={group}
                        showElementsList={showElementsList}
                        setChecklistGroup={setChecklistGroup}
                        setIsUpperElementDrag={setIsUpperElementDrag}
                        setIsLowerElementDrag={setIsLowerElementDrag}
                        isUpperElementDrag={isUpperElementDrag}
                        openAllDropdown={openAllDropdown}
                        onChecklistGroupDragEnd={handleDragEnd}
                        draggable={index !== 0}
                        deletable={index !== 0}
                        systemUserAccess={systemUserAccess}
                        userAccess={userAccess}
                        editGroup={editGroup}
                        loadChecklist={loadChecklist}
                        createElement={createElement}
                        showElementsArr={showElementsArr}
                        optionsQueryParams={{
                            ...optionsQueryParams,
                            entity: "chat-chek-list",
                            action: undefined,
                            entityId: undefined,
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

export default React.memo(TaskChecklist);